import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "../../lib/api";
import { PageHeader, Card, Loading } from "../../components/admin/common";
import {
  IndianRupee, Clock, XCircle, CheckCircle2, Copy, GraduationCap,
  Building2, BookOpen, Plus, FileDown, ArrowUpRight,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import RevenueAnalytics from "../../components/admin/RevenueAnalytics";

const CARDS = [
  { key: "pending_count", label: "Pending", icon: Clock, color: "text-amber-600 bg-amber-50", to: "/admin/registrations?primary=pending" },
  { key: "completed_count", label: "Completed", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50", to: "/admin/registrations?primary=completed" },
  { key: "rejected_count", label: "Rejected", icon: XCircle, color: "text-rose-600 bg-rose-50", to: "/admin/registrations?primary=rejected" },
  { key: "duplicate_count", label: "Duplicate", icon: Copy, color: "text-fuchsia-600 bg-fuchsia-50", to: "/admin/registrations?primary=duplicate" },
  { key: "verified_amount", label: "Collected", icon: IndianRupee, color: "text-teal-600 bg-teal-50", money: true, to: "/admin/registrations?primary=completed" },
  { key: "students", label: "Students", icon: GraduationCap, color: "text-sky-600 bg-sky-50", to: "/admin/students" },
  { key: "schools", label: "Schools", icon: Building2, color: "text-indigo-600 bg-indigo-50", to: "/admin/schools" },
  { key: "active_programs", label: "Active Pages", icon: BookOpen, color: "text-blue-600 bg-blue-50", to: "/admin/pages" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" subtitle="Live overview of registrations and payments across GDIOT PAY.">
        <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md shadow-blue-500/20 flex items-center gap-2 cursor-pointer" onClick={() => navigate("/admin/pages/new")} data-testid="quick-create-page">
          <Plus className="w-4 h-4" /> Create Page
        </button>
        <button className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium py-2.5 px-4 rounded-xl border border-slate-200 flex items-center gap-2 cursor-pointer" onClick={() => navigate("/admin/reports")} data-testid="quick-export">
          <FileDown className="w-4 h-4" /> Export Data
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <button key={c.key} onClick={() => navigate(c.to)} data-testid={`stat-${c.key}`}
            className="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}><c.icon className="w-5 h-5" /></div>
              <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
            </div>
            <div className="mt-3 font-head text-2xl font-extrabold text-slate-900">{c.money ? inr(stats[c.key]) : (stats[c.key] ?? 0).toLocaleString("en-IN")}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
          </button>
        ))}
      </div>

      <RevenueAnalytics />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-head font-semibold text-slate-900 mb-4">Registrations — last 7 days</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.trend} margin={{ left: -20, right: 8, top: 8 }}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Area type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2.5} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-head font-semibold text-slate-900 mb-4">School Distribution</h3>
          <DistList data={stats.school_distribution} empty="No school data yet" />
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-head font-semibold text-slate-900 mb-4">Program Distribution</h3>
        <DistList data={stats.program_distribution} empty="No program data yet" wide />
      </Card>
    </div>
  );
}

function DistList({ data, empty, wide }) {
  if (!data || data.length === 0) return <p className="text-sm text-slate-400 py-6 text-center">{empty}</p>;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className={`space-y-3 ${wide ? "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 space-y-0" : ""}`}>
      {data.map((d) => (
        <div key={d.name}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-700 font-medium truncate">{d.name}</span>
            <span className="text-slate-500 font-mono">{d.count}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${(d.count / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
